import { IconPlusSquare } from "@/assets/icons/geist/IconPlusSquare";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { Banner } from "@/components/flexkit/Banner";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Dialog, DialogContent } from "@/components/podkit/dialog/Dialog";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import {
    RunnerDetailsSection,
    type RunnerDetailsSectionProps,
    type SectionStatus,
} from "@/components/runners/details/RunnerDetailsSection";
import { useProviders } from "@/components/runners/details/source-control-providers.tsx";
import { ProviderRow, SourceControlProviderAddModal } from "@/components/runners/details/SourceControlProviderAddModal";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { SourceControlProviderModal } from "@/components/runners/details/SourceControlProviderModal";
import { SourceControlProviderRemoveModal } from "@/components/runners/details/SourceControlProviderRemoveModal";

import { useMembership } from "@/hooks/use-membership";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useFlagGroupManagementEnabled } from "@/hooks/use-flag";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import type { TrackLocation } from "@/hooks/use-segment";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import {
    type RunnerConfigurationSchema_SCMConfigSchema,
    type RunnerKind,
    SCMIntegration,
    RunnerProvider,
} from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import type React from "react";
import { type FC, useCallback, useState } from "react";

export type Provider = {
    schema: RunnerConfigurationSchema_SCMConfigSchema;
    name: string;
    state: ProviderState;
    analyticsName?: string;
};
export type ProviderState = "coming-soon" | "available";

type ModalState =
    | { type: "closed" }
    | { type: "add-provider" }
    | {
          type: "edit-provider";
          schema: RunnerConfigurationSchema_SCMConfigSchema;
          integration: SCMIntegration;
      }
    | {
          type: "remove-provider";
          integration: SCMIntegration;
      };

export const SourceControlProviderSection: FC<{
    runner: PlainMessage<Runner>;
    schemas?: RunnerConfigurationSchema_SCMConfigSchema[];
    hasStack: boolean;
    "data-track-location": (modalState: ModalState) => TrackLocation;
}> = ({ runner, schemas, hasStack, "data-track-location": dataTrackLocation }) => {
    return (
        <SourceControlProvider
            runner={runner}
            schemas={schemas}
            hasStack={hasStack}
            Container={RunnerDetailsSection}
            withDescription={true}
            data-track-location={dataTrackLocation}
        />
    );
};

export const SourceControlProvider: FC<{
    runner: PlainMessage<Runner>;
    schemas?: RunnerConfigurationSchema_SCMConfigSchema[];
    hasStack: boolean;
    Container: React.ComponentType<RunnerDetailsSectionProps>;
    withDescription: boolean;
    "data-track-location": (modalState: ModalState) => TrackLocation;
}> = ({ runner, schemas, hasStack, Container, withDescription, "data-track-location": dataTrackLocation }) => {
    const {
        data: integrations,
        isLoading: isLoadingIntegrations,
        error,
    } = useListRunnerSCMIntegrations(runner.runnerId);
    const { membership, isPending: isLoadingMembership } = useMembership();
    const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();
    const { hasPermission } = useResourcePermissions({ enabled: isGroupManagementEnabled });
    const isLoading = isLoadingIntegrations || isLoadingMembership;
    const allProviders = useProviders();

    const [modalState, setModalState] = useState<ModalState>({ type: "closed" });

    const onCloseModal = (open: boolean) => {
        if (!open) {
            setModalState({ type: "closed" });
        }
    };

    const onEditProvider = useCallback(
        (integration: SCMIntegration, schema: RunnerConfigurationSchema_SCMConfigSchema) => {
            setModalState({
                type: "edit-provider",
                schema: schema,
                integration,
            });
        },
        [setModalState],
    );

    const onRemoveProvider = useCallback(
        (integration: SCMIntegration) => {
            setModalState({
                type: "remove-provider",
                integration,
            });
        },
        [setModalState],
    );

    const onBack = useCallback(() => {
        setModalState({ type: "add-provider" });
    }, [setModalState]);

    const onAddProvider = useCallback(() => {
        setModalState({ type: "add-provider" });
    }, [setModalState]);

    const onProviderCreate = useCallback(
        (provider: Provider) => {
            setModalState({
                type: "edit-provider",
                schema: provider.schema,
                integration: new SCMIntegration({ scmId: provider.schema.scmId }),
            });
        },
        [setModalState],
    );

    const hasConfiguredProviders = integrations && integrations.length > 0;
    const isManagedRunner = runner.provider === RunnerProvider.MANAGED;
    let status: SectionStatus = "loading";
    if (!isLoading) {
        if (!hasStack) {
            status = "locked";
        } else if (!schemas?.length) {
            status = "initializing";
        } else {
            status = hasConfiguredProviders ? "complete" : "required";
        }
    }

    const providers: Provider[] = [];

    if (schemas?.length) {
        schemas.forEach((scm) => {
            providers.push({ schema: scm, name: scm.name, state: "available" });
        });

        // Add default providers that are not in the list of configured providers, but in a "coming-soon" state.
        allProviders.forEach((provider) => {
            if (!schemas.map((provider) => provider.scmId).includes(provider.schema.scmId)) {
                providers.push({
                    ...provider,
                    state: "coming-soon",
                });
            }
        });
    }

    const ready = status !== "locked" && status !== "initializing";

    // Fallback to admin check when feature flag is disabled
    const isAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const canCreateSCM = isGroupManagementEnabled
        ? hasPermission(ResourceType.RUNNER, runner.runnerId, "runnerscmintegration:create")
        : isAdmin;
    const canUpdateSCM = isGroupManagementEnabled
        ? hasPermission(ResourceType.RUNNER, runner.runnerId, "runnerscmintegration:update")
        : isAdmin;

    const readOnly = !ready || !canUpdateSCM || isManagedRunner;

    return (
        <Container
            status={status}
            title={withDescription ? "Configure repository access" : undefined}
            hideStatus={isManagedRunner}
        >
            <div className="flex flex-col gap-1" data-testid="scm-section">
                <SkeletonBlock ready={!isLoading} className="mt-1 h-[100px]">
                    <div className="flex flex-col gap-4">
                        {withDescription && !isManagedRunner && (
                            <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
                                <Text className={cn(!ready && "text-content-secondary", "text-base")}>
                                    In order for your runner to access your repos, you&#x2019;ll need to configure your
                                    repository provider (GitHub, GitLab, Bitbucket or Azure DevOps).{" "}
                                    {ready && (
                                        <ExternalLink
                                            className="font-bold"
                                            href="https://ona.com/docs/ona/source-control/overview"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Read the docs.
                                        </ExternalLink>
                                    )}
                                </Text>
                            </div>
                        )}
                        <ProviderConfigurations
                            ready={ready}
                            error={error}
                            readOnly={readOnly}
                            canAddProvider={canCreateSCM && !isManagedRunner}
                            providers={providers}
                            runnerKind={runner.kind}
                            integrations={integrations || []}
                            onAddProvider={onAddProvider}
                            onProviderCreate={onProviderCreate}
                            onEditProvider={onEditProvider}
                            isManagedRunner={isManagedRunner}
                        />
                    </div>
                </SkeletonBlock>
            </div>
            {modalState.type !== "closed" && (
                <Dialog open onOpenChange={onCloseModal}>
                    <DialogContent className="max-w-[600px]" data-track-location={dataTrackLocation(modalState)}>
                        {modalState.type == "add-provider" && (
                            <SourceControlProviderAddModal
                                providers={providers}
                                runnerKind={runner.kind}
                                onProviderCreate={onProviderCreate}
                                integrations={integrations || []}
                            />
                        )}
                        {modalState.type == "remove-provider" && (
                            <SourceControlProviderRemoveModal
                                runnerId={runner.runnerId}
                                integration={modalState.integration}
                                onClose={() => onCloseModal(false)}
                            />
                        )}
                        {modalState.type == "edit-provider" && (
                            <SourceControlProviderModal
                                runnerId={runner.runnerId}
                                schema={modalState.schema}
                                integration={modalState.integration}
                                onClose={() => onCloseModal(false)}
                                onBack={() => onBack()}
                                onRemove={() => onRemoveProvider(modalState.integration)}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </Container>
    );
};

export const ProviderConfigurations: FC<{
    ready: boolean;
    error: Error | null;
    canAddProvider: boolean;
    providers: Provider[];
    integrations: SCMIntegration[];
    runnerKind: RunnerKind;
    readOnly: boolean;
    onAddProvider: () => void;
    onEditProvider: (integration: SCMIntegration, schema: RunnerConfigurationSchema_SCMConfigSchema) => void;
    onProviderCreate: (provider: Provider) => void;
    isManagedRunner: boolean;
}> = ({
    ready,
    error,
    canAddProvider,
    providers,
    runnerKind,
    integrations,
    readOnly,
    onAddProvider,
    onEditProvider,
    onProviderCreate,
    isManagedRunner,
}) => {
    if (!error && integrations.length === 0 && providers.length > 0) {
        return (
            <EmptyProviderConfiguration
                providers={providers}
                runnerKind={runnerKind}
                readOnly={readOnly}
                onProviderCreate={onProviderCreate}
                integrations={integrations}
                isManagedRunner={isManagedRunner}
            />
        );
    }

    return (
        <div className="flex w-full flex-col gap-2">
            {error && (
                <div className="flex flex-col items-center">
                    <Text className="text-lg font-bold text-content-negative">Failed to list providers</Text>
                    <Text className="text-base text-content-negative">
                        Please try to refresh the page and contact support if the issue persists.
                    </Text>
                    <ErrorMessage error={error} />
                </div>
            )}
            {integrations.map((integration) => (
                <ProviderConfiguration
                    key={`${integration.scmId}-${integration.host}`}
                    integration={integration}
                    providers={providers}
                    readOnly={readOnly}
                    onEditProvider={onEditProvider}
                    isManagedRunner={isManagedRunner}
                />
            ))}
            {ready && (
                <Button
                    data-testid="add-provider"
                    variant="ghost"
                    onClick={onAddProvider}
                    size="lg"
                    LeadingIcon={IconPlusSquare}
                    disabled={readOnly || !canAddProvider}
                    className="group w-full rounded-xl border-[1px] border-dashed px-3 py-2 hover:bg-surface-02"
                    data-tracking-id="add-scm-provider-scm-provider"
                >
                    <Text className="flex h-9 w-full items-center group-disabled:text-content-secondary">
                        Add a new provider
                    </Text>
                </Button>
            )}
            {isManagedRunner && (
                <Banner
                    variant="info"
                    className="mt-2"
                    text={
                        <div>
                            <div className="font-medium">Repository access is managed by Ona Cloud</div>
                            <div className="text-sm">
                                Connect to private or self-hosted Git servers (GitHub Enterprise, GitLab self-hosted,
                                etc.) with Enterprise tier.{" "}
                                <a
                                    href="https://ona.com/docs/ona/runners/ona-cloud"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline hover:no-underline"
                                >
                                    Learn more
                                </a>
                            </div>
                        </div>
                    }
                />
            )}
        </div>
    );
};

const ProviderConfiguration: FC<{
    integration: SCMIntegration;
    providers: Provider[];
    readOnly: boolean;
    onEditProvider: (integration: SCMIntegration, schema: RunnerConfigurationSchema_SCMConfigSchema) => void;
    isManagedRunner: boolean;
}> = ({ integration, providers, readOnly, onEditProvider, isManagedRunner }) => {
    const authMethods = [];
    if (integration.oauth) {
        authMethods.push("OAuth");
    }
    if (integration.pat) {
        authMethods.push("Personal Access Token");
    }

    const provider = providers.find((provider) => provider.schema.scmId === integration.scmId);

    return (
        <div className="flex justify-between gap-4 rounded-xl border-0.5 border-border-base bg-surface-primary px-3 py-2">
            <div className="flex items-center gap-4">
                <SourceControlProviderIcon
                    scmId={integration.scmId}
                    className="hidden size-8 text-content-primary md:block"
                />
                <div className="flex flex-col">
                    <div className="flex gap-1">
                        <Text className="text-base font-bold">{integration.scmId}</Text>
                        <Text className="text-base text-content-secondary">{integration.host}</Text>
                    </div>
                    <Text className="text-sm">Auth: {authMethods.join(" or ")}</Text>
                </div>
            </div>
            {!isManagedRunner && (
                <Button
                    variant="secondary"
                    disabled={!provider || readOnly}
                    data-testid={`edit-provider-${integration.id}`}
                    onClick={() => {
                        if (provider) {
                            onEditProvider(integration, provider?.schema);
                        }
                    }}
                    data-tracking-id="edit-scm-provider-scm-provider"
                >
                    Edit
                </Button>
            )}
        </div>
    );
};

const EmptyProviderConfiguration: FC<{
    readOnly: boolean;
    providers: Provider[];
    runnerKind: RunnerKind;
    onProviderCreate: (provider: Provider) => void;
    integrations: SCMIntegration[];
    isManagedRunner: boolean;
}> = ({ readOnly, providers, runnerKind, onProviderCreate, integrations, isManagedRunner }) => {
    // Filter out Azure DevOps provider if there are no existing Azure DevOps integrations
    const hasExistingAzureDevOpsIntegration = integrations.some((integration) => integration.scmId === "azuredevops");

    const filteredProviders = providers.filter((provider) => {
        // If it's not Azure DevOps, always include it
        if (provider.schema.scmId !== "azuredevops") {
            return true;
        }

        // For Azure DevOps, only include if there are existing integrations
        return hasExistingAzureDevOpsIntegration;
    });

    return (
        <div className="flex w-full flex-col items-center justify-center gap-3 rounded-lg">
            <div className="flex w-full flex-col gap-2">
                {filteredProviders.map((provider) => (
                    <ProviderRow
                        key={provider.schema.scmId}
                        provider={provider}
                        runnerKind={runnerKind}
                        onProviderCreate={isManagedRunner ? undefined : onProviderCreate}
                        readOnly={readOnly || isManagedRunner}
                    />
                ))}
            </div>
            {isManagedRunner && (
                <Banner
                    variant="info"
                    className="mt-2 w-full"
                    text={
                        <div>
                            <div className="font-medium">Repository access is managed by Ona Cloud</div>
                            <div className="text-sm">
                                Connect to private or self-hosted Git servers (GitHub Enterprise, GitLab self-hosted,
                                etc.) with Enterprise tier.{" "}
                                <a
                                    href="https://ona.com/docs/ona/runners/ona-cloud"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline hover:no-underline"
                                >
                                    Learn more
                                </a>
                            </div>
                        </div>
                    }
                />
            )}
        </div>
    );
};
